import { useState, type ReactNode } from 'react';
import { ChevronDown, ChevronUp, Target, Shield, Zap, TrendingUp, TriangleAlert as AlertTriangle, Layers, Cpu, Rocket, DollarSign, Users } from 'lucide-react';
import type { VendorIntelligence } from '../../types';
import FormattedText from '../common/FormattedText';

interface Props {
  intelligence: VendorIntelligence;
}

export default function WhoWeAreTab({ intelligence }: Props) {
  const [expandedProblem, setExpandedProblem] = useState<number | null>(null);
  const [expandedVertical, setExpandedVertical] = useState<string | null>(null);
  const [showFullTech, setShowFullTech] = useState(false);
  const [showFullRoadmap, setShowFullRoadmap] = useState(false);
  const [showFullRisks, setShowFullRisks] = useState(false);

  return (
    <div className="space-y-8">
      <div className="bg-app-surface rounded-lg border border-forest-100 p-8">
        <h2 className="text-xl font-bold text-app-ink mb-4">Who We Are</h2>
        <FormattedText text={intelligence.who_we_are} className="text-sm text-app-ink" />
      </div>

      <div className="bg-app-surface rounded-lg border border-app-line shadow-none p-6">
        <div className="flex items-center gap-2 mb-4">
          <Target className="w-5 h-5 text-forest-600" />
          <h3 className="text-base font-semibold text-app-ink">Value Proposition</h3>
        </div>
        <FormattedText text={intelligence.value_proposition} className="text-sm text-app-ink" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="bg-app-surface rounded-lg border border-app-line shadow-none p-6">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-5 h-5 text-app-focus" />
            <h3 className="text-base font-semibold text-app-ink">Ideal Customer Profile</h3>
          </div>
          <FormattedText text={intelligence.icp_summary} className="text-sm text-app-ink" />
        </div>
        <div className="bg-app-surface rounded-lg border border-app-line shadow-none p-6">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="w-5 h-5 text-app-warning" />
            <h3 className="text-base font-semibold text-app-ink">Market Wedge</h3>
          </div>
          <FormattedText text={intelligence.market_wedge} className="text-sm text-app-ink" />
        </div>
      </div>

      <div className="bg-app-surface rounded-lg border border-app-line shadow-none p-6">
        <div className="flex items-center gap-2 mb-4">
          <Shield className="w-5 h-5 text-forest-600" />
          <h3 className="text-base font-semibold text-app-ink">Executive Overview</h3>
        </div>
        <FormattedText text={intelligence.executive_overview} className="text-sm text-app-ink" />
      </div>

      {intelligence.market_position?.category && (
        <div className="bg-app-surface rounded-lg border border-app-line shadow-none p-6">
          <h3 className="text-base font-semibold text-app-ink mb-4">Market Position</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {intelligence.market_position.category && (
              <div className="bg-app-canvas rounded-lg p-4">
                <p className="text-xs font-medium text-app-muted mb-1">Category</p>
                <p className="text-sm text-app-ink">{intelligence.market_position.category}</p>
              </div>
            )}
            {intelligence.market_position.analyst_positioning && (
              <div className="bg-app-canvas rounded-lg p-4">
                <p className="text-xs font-medium text-app-muted mb-1">Analyst Positioning</p>
                <p className="text-sm text-app-ink">{intelligence.market_position.analyst_positioning}</p>
              </div>
            )}
            {intelligence.market_position.market_perception && (
              <div className="bg-app-canvas rounded-lg p-4">
                <p className="text-xs font-medium text-app-muted mb-1">Market Perception</p>
                <p className="text-sm text-app-ink">{intelligence.market_position.market_perception}</p>
              </div>
            )}
            {intelligence.market_position.strategic_ambition && (
              <div className="bg-app-canvas rounded-lg p-4">
                <p className="text-xs font-medium text-app-muted mb-1">Strategic Ambition</p>
                <p className="text-sm text-app-ink">{intelligence.market_position.strategic_ambition}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {(intelligence.key_differentiators || []).length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Zap className="w-5 h-5 text-forest-600" />
            <h3 className="text-base font-semibold text-app-ink">Key Differentiators</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {intelligence.key_differentiators.map((d, i) => (
              <div key={i} className="border border-app-line bg-app-surface-raised p-5 transition-colors hover:border-app-line-strong">
                <h4 className="text-sm font-semibold text-forest-900 mb-2">{d.title}</h4>
                <p className="text-xs text-forest-700 leading-relaxed">{d.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {(intelligence.core_problems || []).length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Layers className="w-5 h-5 text-app-focus" />
            <h3 className="text-base font-semibold text-app-ink">Core Problems We Solve</h3>
          </div>
          <div className="space-y-3">
            {intelligence.core_problems.map((p, i) => (
              <div key={i} className="bg-app-surface rounded-lg border border-app-line shadow-none overflow-hidden">
                <button
                  onClick={() => setExpandedProblem(expandedProblem === i ? null : i)}
                  className="w-full flex items-center justify-between p-4 hover:bg-app-canvas transition-colors text-left"
                >
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-app-ink">{p.title}</h4>
                    <p className="text-xs text-app-muted mt-0.5 line-clamp-1">{p.problem_statement}</p>
                  </div>
                  {expandedProblem === i ? <ChevronUp className="w-4 h-4 text-app-muted flex-shrink-0 ml-3" /> : <ChevronDown className="w-4 h-4 text-app-muted flex-shrink-0 ml-3" />}
                </button>
                {expandedProblem === i && (
                  <div className="px-4 pb-4 space-y-3">
                    <div className="bg-app-canvas rounded-lg p-3">
                      <p className="text-xs font-medium text-app-muted mb-1">Problem Statement</p>
                      <p className="text-sm text-app-ink leading-relaxed">{p.problem_statement}</p>
                    </div>
                    <div className="bg-app-canvas rounded-lg p-3">
                      <p className="text-xs font-medium text-app-muted mb-1">Commercial Impact</p>
                      <p className="text-sm text-app-ink leading-relaxed">{p.commercial_impact}</p>
                    </div>
                    <div className="bg-app-canvas rounded-lg p-3">
                      <p className="text-xs font-medium text-app-muted mb-1">How We Address It</p>
                      <p className="text-sm text-app-ink leading-relaxed">{p.how_addressed}</p>
                    </div>
                    <div className="bg-app-canvas rounded-lg p-3">
                      <p className="text-xs font-medium text-app-muted mb-1">What Makes Our Approach Different</p>
                      <p className="text-sm text-app-ink leading-relaxed">{p.structural_difference}</p>
                    </div>
                    {p.evidence && (
                      <div className="bg-app-canvas rounded-lg p-3">
                        <p className="text-xs font-medium text-app-muted mb-1">Evidence</p>
                        <p className="text-sm text-app-ink leading-relaxed">{p.evidence}</p>
                      </div>
                    )}
                    {p.sales_benefits && (
                      <div className="bg-forest-50 rounded-lg p-3">
                        <p className="text-xs font-medium text-forest-700 mb-1">Sales Benefits</p>
                        <p className="text-sm text-forest-800 leading-relaxed">{p.sales_benefits}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {(intelligence.industry_verticals || []).length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-forest-600" />
            <h3 className="text-base font-semibold text-app-ink">Industry Vertical Strength</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {intelligence.industry_verticals.map((v, i) => (
              <div
                key={i}
                className="bg-app-surface rounded-lg border border-app-line shadow-none p-5 cursor-pointer hover:shadow-none hover:border-forest-200 transition"
                onClick={() => setExpandedVertical(expandedVertical === v.name ? null : v.name)}
              >
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-app-ink">{v.name}</h4>
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-2.5 bg-app-line rounded-full overflow-hidden" role="progressbar" aria-label={`${v.name} strength`} aria-valuemin={0} aria-valuemax={10} aria-valuenow={v.score || 0}>
                      <div
                        className={`h-full rounded-full transition ${
                          v.score >= 8 ? 'bg-app-positive' : v.score >= 5 ? 'bg-app-warning' : 'bg-app-critical'
                        }`}
                        style={{ width: `${(v.score || 0) * 10}%` }}
                      />
                    </div>
                    <span className="text-xs font-bold text-app-ink">{v.score}/10</span>
                  </div>
                </div>
                <p className="text-xs text-app-muted leading-relaxed line-clamp-2">{v.challenges}</p>

                {expandedVertical === v.name && (
                  <div className="mt-4 pt-4 border-t border-app-line space-y-3">
                    <DetailBlock label="Why This Technology Fits" value={v.structural_fit} />
                    <DetailBlock label="Unique Differentiators" value={v.differentiators} />
                    <DetailBlock label="Competitive Edge" value={v.competitive_edge} />
                    {v.case_studies?.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-app-muted mb-1.5">Success Stories / Case Studies</p>
                        <ul className="space-y-1">
                          {v.case_studies.map((cs, j) => (
                            <li key={j} className="text-xs text-app-ink bg-app-positive/10 rounded-lg px-3 py-2">
                              {cs}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <DetailBlock label="Expansion Potential" value={v.expansion_potential} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {intelligence.technical_architecture && (
        <ExpandableTextSection
          icon={<Cpu className="w-5 h-5 text-app-muted" />}
          title="Technical Architecture"
          content={intelligence.technical_architecture}
          isOpen={showFullTech}
          onToggle={() => setShowFullTech(!showFullTech)}
        />
      )}

      {intelligence.current_capabilities && (
        <div className="bg-app-surface rounded-lg border border-app-line shadow-none p-6">
          <h3 className="text-base font-semibold text-app-ink mb-4">Current Capabilities</h3>
          <div className="space-y-4">
            {(intelligence.current_capabilities.generally_available || []).length > 0 && (
              <div>
                <p className="text-xs font-semibold text-app-muted mb-2 uppercase tracking-wider">Generally Available</p>
                <div className="flex flex-wrap gap-2">
                  {intelligence.current_capabilities.generally_available!.map((c, i) => (
                    <span key={i} className="px-3 py-1.5 bg-app-positive/10 text-app-positive text-xs rounded-lg font-medium">{c}</span>
                  ))}
                </div>
              </div>
            )}
            {(intelligence.current_capabilities.enterprise_tier || []).length > 0 && (
              <div>
                <p className="text-xs font-semibold text-app-muted mb-2 uppercase tracking-wider">Enterprise Tier</p>
                <div className="flex flex-wrap gap-2">
                  {intelligence.current_capabilities.enterprise_tier!.map((c, i) => (
                    <span key={i} className="px-3 py-1.5 bg-app-focus/10 text-app-focus text-xs rounded-lg font-medium">{c}</span>
                  ))}
                </div>
              </div>
            )}
            {(intelligence.current_capabilities.recently_released || []).length > 0 && (
              <div>
                <p className="text-xs font-semibold text-app-muted mb-2 uppercase tracking-wider">Recently Released</p>
                <div className="flex flex-wrap gap-2">
                  {intelligence.current_capabilities.recently_released!.map((c, i) => (
                    <span key={i} className="px-3 py-1.5 bg-app-warning/10 text-app-warning text-xs rounded-lg font-medium">{c}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {intelligence.roadmap && (
        <ExpandableTextSection
          icon={<Rocket className="w-5 h-5 text-app-muted" />}
          title="Roadmap & Forward Strategy"
          content={intelligence.roadmap}
          isOpen={showFullRoadmap}
          onToggle={() => setShowFullRoadmap(!showFullRoadmap)}
        />
      )}

      {intelligence.pricing_positioning && (
        <div className="bg-app-surface rounded-lg border border-app-line shadow-none p-6">
          <div className="flex items-center gap-2 mb-3">
            <DollarSign className="w-5 h-5 text-app-muted" />
            <h3 className="text-base font-semibold text-app-ink">Pricing Positioning</h3>
          </div>
          <FormattedText text={intelligence.pricing_positioning} className="text-sm text-app-ink" />
        </div>
      )}

      {intelligence.risks_and_gaps && (
        <ExpandableTextSection
          icon={<AlertTriangle className="w-5 h-5 text-app-warning" />}
          title="Risks, Gaps & Competitive Weaknesses"
          content={intelligence.risks_and_gaps}
          isOpen={showFullRisks}
          onToggle={() => setShowFullRisks(!showFullRisks)}
        />
      )}
    </div>
  );
}

function DetailBlock({ label, value }: { label: string; value: string }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-xs font-medium text-app-muted mb-0.5">{label}</p>
      <p className="text-xs text-app-ink leading-relaxed">{value}</p>
    </div>
  );
}

function ExpandableTextSection({ icon, title, content, isOpen, onToggle }: {
  icon: ReactNode;
  title: string;
  content: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const preview = content.length > 300 ? content.substring(0, 300) + '...' : content;

  return (
    <div className="bg-app-surface rounded-lg border border-app-line shadow-none p-6">
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <h3 className="text-base font-semibold text-app-ink">{title}</h3>
      </div>
      <FormattedText text={isOpen ? content : preview} className="text-sm text-app-ink" />
      {content.length > 300 && (
        <button onClick={onToggle} className="mt-3 text-xs font-medium text-forest-600 hover:text-forest-800 flex items-center gap-1">
          {isOpen ? <><ChevronUp className="w-3.5 h-3.5" /> Show less</> : <><ChevronDown className="w-3.5 h-3.5" /> Read more</>}
        </button>
      )}
    </div>
  );
}
